<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import TipTapEditor from "@/components/wysiwyg/TipTapEditor.vue";
import TranslatableForm from "@/components/TranslatableForm.vue";
import MarketingMessageImage from "@/components/marketing-message/MarketingMessageImage.vue";

const props = defineProps({
  marketingMessage: {
    default: () => ({})
  },
  media: {
    type: Array,
    default: () => []
  },
  mediaUploadUrl: {
    type: String,
    required: true,
  },
  locales: {
    type: Array,
    required: true,
  },
  defaultLocale: {
    type: String,
    default: 'en'
  },
});

const emit = defineEmits(['message-updated', 'lang-change']);

const message = ref(props.marketingMessage);
const lang = ref(null);

watch(() => props.marketingMessage, (value) => {
  message.value = value;
});

watch(message, (newValue) => {
  emit('message-updated', newValue);
}, { deep: true });

const updateTranslation = (field, lang, value) => {
  let translationIndex = message.value.translations.findIndex(
    (translation) => translation.locale === lang
  );

  if (translationIndex < 0) {
    let translation = {
      locale: lang,
    };
    translation[field] = value;
    message.value.translations.push(translation);
    return;
  }

  let translation = message.value.translations.find(
    (translation) => translation.locale === lang
  );
  translation[field] = value;
  message.value.translations.splice(translationIndex, 1, translation);
};

const localMedia = ref([...props.media]);
const onFilesUploadedToCollection = (collection, files) => {
  if (
    ['marketing-message-header', 'marketing-message-footer'].includes(collection)
    && files.length > 0)
  {
    const file = files[0];
    const existingMediaIndex = localMedia.value.findIndex(m => m.collection_name === collection);
    if (existingMediaIndex >= 0) {
      localMedia.value[existingMediaIndex] = file;
    } else {
      localMedia.value.push(file);
    }
  } else {
    // for other collections, just append the files to the localMedia collection
    localMedia.value.push(...files);
  }
  emit('message-updated', message.value)
};
const onClickDeleteMedia = async (collection) => {
  const mediaIndex = localMedia.value.findIndex(m => m.collection_name === collection);
  if (
    ['marketing-message-header', 'marketing-message-footer'].includes(collection)
    && mediaIndex >= 0
    && localMedia.value[mediaIndex].links?.delete
  ) {
    await fetch(localMedia.value[mediaIndex].links.delete, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-TOKEN': window.Laravel.csrfToken || document.querySelector('meta[name="csrf-token"]').getAttribute('content') || ''
      }
    });
    localMedia.value.splice(mediaIndex, 1);
  }
  emit('message-updated', message.value)
};

</script>

<template>
  <div>
    <div class="form-group">
      <label for="from_name">
        {{ $t("Sender’s Name")}}
      </label>
      <input type="text" id="from_name" name="from_name" v-model="message.from_name" class="form-control">
    </div>
    <div class="form-group">
      <label for="from_email">
        {{ $t('Reply-To Email Address') }} {{ $t('(optional)')}}
      </label>
      <input type="email" id="from_email" name="from_email" v-model="message.from_email" class="form-control">
    </div>

    <hr>
    <MarketingMessageImage
      :label="$t('Header Image')"
      input-id="frmMessageHeaderImage"
      :media="localMedia.find(m => m.collection_name === 'marketing-message-header')"
      :upload-url="props.mediaUploadUrl"
      collection-name="marketing-message-header"
      @files-uploaded="onFilesUploadedToCollection('marketing-message-header', $event)"
      @delete="onClickDeleteMedia('marketing-message-header')"
    />

    <hr>

    <TranslatableForm
      :data-locales="locales"
      :data-default-locale="defaultLocale"
      v-on:lang-change="emit('lang-change', $event)">
      <template #default="{ lang }">
        <div class="form-group">
          <label>
            {{ $t('Email Subject') }} *
          </label>
          <div v-for="translation in message.translations">
            <input
              type="text"
              :name="'subject[' + translation.locale + ']'"
              :value="translation.subject"
              @input="updateTranslation('subject', translation.locale, $event.target.value)"
              class="form-control"
              :data-locale="translation.locale"
              v-show="lang === translation.locale"
            >
          </div>
        </div>

        <!-- https://forum.vuejs.org/t/plain-prop-string-containing-mustaches/10825/4 -->
        <div>
          <label>
            {{ $t('Email Message') }} *
          </label>
          <div v-for="translation in message.translations">
            <TipTapEditor
              v-show="lang === translation.locale"
              :name="'message[' + translation.locale + ']'"
              :value="translation.message"
              @input="updateTranslation('message', translation.locale, $event)"
              with-media-library
              media-collection-name="marketing-message-content"
              :media-upload-url="props.mediaUploadUrl"
              :media="localMedia.filter(m => m.collection_name === 'marketing-message-content')"
              @media-collection-updated="onFilesUploadedToCollection('marketing-message-content', $event)"
              :data-locale="translation.locale"
            >
            </TipTapEditor>
          </div>
        </div>
      </template>
    </TranslatableForm>

    <hr>

    <MarketingMessageImage
      :label="$t('Footer Image')"
      input-id="frmMessageFooterImage"
      :media="localMedia.find(m => m.collection_name === 'marketing-message-footer')"
      :upload-url="props.mediaUploadUrl"
      collection-name="marketing-message-footer"
      @files-uploaded="onFilesUploadedToCollection('marketing-message-footer', $event)"
      @delete="onClickDeleteMedia('marketing-message-footer')"
    />
  </div>
</template>